import { useCallback } from 'react';

import { Trash2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { pushModal } from '@/redux/modals/actions';
import { EmailIntegration } from '@/shared/types/integration';

interface DeleteButtonProps {
  integration: EmailIntegration | undefined;
  canWrite: boolean;
}

/**
 * DeleteButton - Button component for deleting an email integration
 *
 * @param {DeleteButtonProps} props - Component props
 * @param {EmailIntegration | undefined} props.integration - The email integration to delete
 * @param {boolean} props.canWrite - Whether the user has write permissions
 * @returns {JSX.Element} The delete button component
 */
export default function DeleteButton({ integration, canWrite }: DeleteButtonProps) {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { integrationId } = useParams();
  const { deleteIntegrationAsync, deleteStatus } = useIntegrations<EmailIntegration>(
    deskId,
    integrationId
  );
  const dispatch = useDispatch();

  const handleClick = useCallback(() => {
    const deleteProps = {
      title: t('email.delete_email_title', { 0: integration?.config?.from_email }),
      subtitle: (
        <Trans
          i18nKey="email.delete_email_description"
          values={{ 0: integration?.config?.from_email }}
        />
      ),
      confirm: true,
      onDelete: () => {
        return deleteIntegrationAsync({ desk_id: deskId, integration_id: integrationId });
      },
    };
    dispatch(pushModal(MODAL_DELETE, deleteProps));
  }, [t, integration?.config?.from_email, dispatch, deleteIntegrationAsync, deskId, integrationId]);

  return (
    <Button
      variant="destructive-ghost"
      className="w-fit"
      disabled={!canWrite}
      onClick={handleClick}
      isLoading={deleteStatus === 'pending'}
    >
      <Trash2Icon className="mr-2 h-4 w-4" />
      {t('email.delete_email')}
    </Button>
  );
}
